import './Table.sass';
import '../../QuestionGptModal/QuestionGptModal.sass';

import moment from 'moment';
import React from 'react';
import { Col, Row } from 'react-bootstrap';

import { SimpleDatePicker } from '../../../../../commonComponents/Datepicker/Datepicker';
import DropDown from '../../../../../commonComponents/DropDown/DropDown';
import ConfirmDeletion from '../../../../../commonComponents/Modals/ConfirmDeletion/ConfirmDeletion';
import { getStatusByName } from '../../../../../constants/assignments';
import { canPerformAction } from '../../../../../utilities/permissionUtils';
import { getValidationSummary } from '../../../../../utilities/questionValidation';
import * as api from '../../../../../utilities/ServiceManager';
import {
  AI_TASK_STATUS,
  AI_TASK_TYPE,
} from '../../../../Questions/commonComponents/Header/constants/constants';
import WritersContainer from '../../../../Writers/components/WritersContainer';
import QuestionGptModal from '../../QuestionGptModal/QuestionGptModalContainer';
import ViewSummaryModal from '../../ViewSummaryModal/ViewSummaryModal';
import AssignmentCommentsContainer from '../AssignmentCommentsContainer';

export default class Table extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submittingAssignmentId: null,
      sortColumn: null,
      sortDirection: 'asc',
    };
  }

  getPermissionPrefix = () => {
    // Check if we're in the Writers view (AssignmentsByWriter component)
    // by checking if writers prop exists
    if (this.props.writers) {
      return 'MissionControl.Writers';
    }
    return 'MissionControl.Assignments';
  };

  componentDidMount() {
    if (this.props.loadingCreateGPTquestions) {
      this.props.toggleModalVisibility({
        modalContent: (
          <QuestionGptModal
            onCancel={this.props.closeModal}
            loadingCreateGPTquestions={this.props.loadingCreateGPTquestions}
            assignment={this.props.loadingCreateGPTquestions}
            getUrlPdf={this.props.getUrlPdf}
          />
        ),
        modalClassName: 'modal-question-container',
      });

      this.props.setLoadingCreateGPTquestion(false);
    }
  }

  viewComments = (assignment) => {
    this.props.openModal({
      modalTitle: 'View comments',
      modalContent: (
        <AssignmentCommentsContainer
          assignment={assignment}
          canAddComment={this.canAddComment(assignment)}
        />
      ),
      cross: true,
      modalClassName: 'viewComments',
    });
  };

  canAddComment = (assignment) => {
    const { permissions, user } = this.props;
    const assignmentPermissions = [
      'MissionControl.Writers.Review',
      'MissionControl.Writers.Publish',
      'MissionControl.Writers.Assign',
      'MissionControl.Writers.Delete',
    ];
    const writerPermissions = [
      'MissionControl.Writers.Start',
      'MissionControl.Writers.Open',
      'MissionControl.Writers.Submit',
    ];
    const isAssignedWriter = assignment.assignedTo?.some(
      (writer) => writer.id === user?.id,
    );
    const hasGlobalPermission = assignmentPermissions.some((permission) =>
      canPerformAction(permission, permissions),
    );
    const hasWriterPermission = writerPermissions.some((permission) =>
      canPerformAction(permission, permissions),
    );

    return hasGlobalPermission || (isAssignedWriter && hasWriterPermission);
  };

  // comments can be opened to read existing ones or to add the first one
  canViewComments = (assignment) =>
    this.renderCommentsNumber(assignment) > 0 || this.canAddComment(assignment);

  renderCommentsNumber = (assignment) => {
    let counter = 0;
    if (assignment && assignment.comments) {
      assignment.comments.forEach((item) => {
        if (item.comment !== '') {
          counter++;
        }
      });
    }

    return counter;
  };

  getSortableValue = (assignment, column) => {
    switch (column) {
      case 'reference':
        return assignment.chapter?.reference?.name?.toLowerCase() ?? '';
      case 'chapter':
        return assignment.chapter?.name?.toLowerCase() ?? '';
      case 'pdfPages':
        return Number(assignment.chapter?.pdfPages) || 0;
      case 'submittedDate':
        return assignment.submittedDate
          ? moment(assignment.submittedDate).valueOf()
          : 0;
      case 'dateAssigned':
        return moment(assignment.createdAt).valueOf();
      case 'lastEdited':
        return moment(assignment.updatedAt).valueOf();
      case 'assignedBy':
        return assignment.createdBy
          ? `${assignment.createdBy.name} ${assignment.createdBy.lastName}`.toLowerCase()
          : '';
      case 'comments':
        return this.renderCommentsNumber(assignment);
      case 'afsc':
        return assignment.studyProgram.typeId?.shortname;
      case 'questions':
        return Number(assignment.amountOfQuestions) || 0;
      default:
        return '';
    }
  };

  sortAssignments = (assignments) => {
    const { sortColumn, sortDirection } = this.state;
    if (!sortColumn) return assignments;

    const sorted = [...assignments].sort((a, b) => {
      const valA = this.getSortableValue(a, sortColumn);
      const valB = this.getSortableValue(b, sortColumn);
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  onSortClick = (column) => {
    if (this.props.activeStatus !== 'Awaiting approval') return;
    this.setState((prevState) => ({
      sortColumn: column,
      sortDirection:
        prevState.sortColumn === column && prevState.sortDirection === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  renderSortArrow = (column) => {
    if (this.state.sortColumn !== column) return null;
    return (
      <span className="sortArrow">
        {this.state.sortDirection === 'asc' ? ' ▲' : ' ▼'}
      </span>
    );
  };

  renderHeader = () => {
    const isSortable = this.props.activeStatus === 'Awaiting approval';
    const showLeadColumn =
      this.props.writers && this.props.activeStatus === 'Working On';

    const dueDateOrSubmittedDateColumn =
      this.props.activeStatus === 'Awaiting approval'
        ? 'submittedDate'
        : 'dueDate';

    const sortableProps = (column, baseClassName = 'tHeader') =>
      isSortable
        ? {
            onClick: () => this.onSortClick(column),
            className: `${baseClassName} sortableHeader`,
          }
        : { className: baseClassName };

    return (
      <Row className="headerContainer">
        {showLeadColumn && (
          <Col md="auto" className="tHeader assignmentLeadColumn" />
        )}
        <Col md={2} {...sortableProps('reference')}>
          Reference{this.renderSortArrow('reference')}
        </Col>
        <Col md={2} {...sortableProps('chapter')}>
          Chapter{this.renderSortArrow('chapter')}
        </Col>
        <Col md={1} {...sortableProps('pdfPages')}>
          Pdf pages{this.renderSortArrow('pdfPages')}
        </Col>
        <Col md={1} {...sortableProps(dueDateOrSubmittedDateColumn)}>
          {dueDateOrSubmittedDateColumn === 'submittedDate'
            ? 'Submitted date'
            : 'Due date'}
          {this.renderSortArrow(dueDateOrSubmittedDateColumn)}
        </Col>
        <Col md={1} {...sortableProps('dateAssigned')}>
          Assigned date{this.renderSortArrow('dateAssigned')}
        </Col>
        <Col md={1} {...sortableProps('lastEdited')}>
          Last edited{this.renderSortArrow('lastEdited')}
        </Col>
        <Col md={1} {...sortableProps('assignedBy')}>
          Assigned by{this.renderSortArrow('assignedBy')}
        </Col>
        <Col md={1} {...sortableProps('comments')}>
          Comments{this.renderSortArrow('comments')}
        </Col>
        {this.props.writers && (
          <Col md={1} {...sortableProps('afsc', 'tHeader centerHeader')}>
            AFSC{this.renderSortArrow('afsc')}
          </Col>
        )}
        <Col md={1} {...sortableProps('questions', 'tHeader centerHeader')}>
          Questions{this.renderSortArrow('questions')}
        </Col>
        <Col md={1} className="tHeader centerHeader">
          Actions
        </Col>
        <Col md={1} className="tHeader" />
      </Row>
    );
  };

  onDateChange = (date, assignment) => {
    let editedAssignment = {
      id: assignment.id,
      dueDate: date.toJSON(),
    };
    if (this.props.writers) {
      editedAssignment = {
        id: assignment.id,
        dueDate: date.toJSON(),
        writerEmail: this.props.selectedWriter.email,
      };
      this.props.onEditAssignment(editedAssignment);
    } else {
      editedAssignment = {
        id: assignment.id,
        dueDate: date.toJSON(),
      };
      this.props.onEditAssignment(editedAssignment);
    }
  };

  getActions = (state, assignment, summary) => {
    const permissions = this.props.permissions;
    const permissionPrefix = this.getPermissionPrefix();

    const hasQuestions =
      assignment &&
      assignment.amountOfQuestions != null &&
      parseInt(assignment.amountOfQuestions, 10) > 0;

    let isGenerateSummary = true;
    if (this.props.summary?.chapter === assignment.chapter.id) {
      isGenerateSummary = false;
    }

    const actions = { buttonText: null, options: [] };

    // Check for AI Questions permission (includes Generate/Regenerate Summary)
    const canCreateAIQuestions = canPerformAction(
      `${permissionPrefix}.CreateAIQuestions`,
      permissions,
    );

    if (canCreateAIQuestions && !assignment.summary && isGenerateSummary) {
      actions.options.push('Generate a Summary');
    } else if (
      assignment.summary ||
      summary?.chapter === assignment.chapter.id
    ) {
      actions.options.push('View a Summary');
      if (canCreateAIQuestions) {
        actions.options.push('Regenerate a Summary');
      }
    }

    switch (state) {
      case 'Not Started':
        const canStart = canPerformAction(
          `${permissionPrefix}.Start`,
          permissions,
        );
        const canAssign = canPerformAction(
          `${permissionPrefix}.Assign`,
          permissions,
        );
        const canDeleteNotStarted = canPerformAction(
          `${permissionPrefix}.Delete`,
          permissions,
        );

        if (canStart) {
          actions.buttonText = 'Start';
        }
        if (canAssign) {
          actions.options.push('Reassign');
        }
        if (canDeleteNotStarted) {
          actions.options.push('Delete');
        }
        break;

      case 'In progress':
        const canOpen = canPerformAction(
          `${permissionPrefix}.Open`,
          permissions,
        );
        const canSubmit = canPerformAction(
          `${permissionPrefix}.Submit`,
          permissions,
        );
        const canDeleteInProgress = canPerformAction(
          `${permissionPrefix}.Delete`,
          permissions,
        );

        if (canOpen) {
          actions.buttonText = 'Open';
        }

        if (canSubmit && hasQuestions) {
          actions.options.push('Submit');
        }

        if (canDeleteInProgress) {
          actions.options.push('Delete');
        }

        if (this.canViewComments(assignment)) {
          actions.options.push('View comments');
        }

        break;

      case 'Submitted':
        const canReview = canPerformAction(
          `${permissionPrefix}.Review`,
          permissions,
        );
        const canDeleteSubmitted = canPerformAction(
          `${permissionPrefix}.Delete`,
          permissions,
        );

        if (canReview) {
          actions.buttonText = 'Review';
        }

        if (canDeleteSubmitted) {
          actions.options.push('Delete');
        }

        if (this.canViewComments(assignment)) {
          actions.options.push('View comments');
        }

        break;

      case 'Completed':
        const canPublish = canPerformAction(
          `${permissionPrefix}.Publish`,
          permissions,
        );
        const canDeleteCompleted = canPerformAction(
          `${permissionPrefix}.Delete`,
          permissions,
        );

        if (canPublish) {
          actions.options.push('Publish');
        }

        if (canDeleteCompleted) {
          actions.options.push('Delete');
        }
        break;

      default:
        break;
    }

    return actions;
  };

  onSubmitAssignment = async (assignment) => {
    if (this.state.submittingAssignmentId === assignment.id) {
      return;
    }

    this.setState({ submittingAssignmentId: assignment.id });
    try {
      // The API checks every question of the chapter and records what it found
      // as the assignment's comments, so the warning only has to carry the
      // counts however many problems there are.
      const { issueCount, questionCount } =
        await api.validateAssignmentSubmission(assignment.id);

      if (issueCount > 0) {
        this.props.showWarning(
          'Cannot submit',
          `${getValidationSummary(
            issueCount,
            questionCount,
          )}. See the assignment comments for the detail.`,
        );
        // The status did not change, so nothing else refreshes the list: pull
        // it again to pick up the comments just logged.
        if (this.props.reloadAssignments) {
          this.props.reloadAssignments();
        }
        return;
      }

      this.onStatusChange(assignment, getStatusByName('Submitted'));
    } catch (exception) {
      this.props.showWarning(
        'Cannot submit',
        'Could not validate the chapter questions. Please try again later.',
      );
    } finally {
      this.setState({ submittingAssignmentId: null });
    }
  };

  getRowState = (assignment) => {
    if (this.props.activeStatus === 'Working On') {
      return assignment.status === 'Not_Started'
        ? 'Not Started'
        : 'In progress';
    }
    if (this.props.activeStatus === 'Awaiting approval') {
      return 'Submitted';
    }
    return this.props.activeStatus;
  };

  onStatusChange = (assignment, newStatus) => {
    let editedAssignment = {};

    if (this.props.writers) {
      editedAssignment = {
        id: assignment.id,
        status: newStatus,
        writerEmail: this.props.selectedWriter.email,
      };
      this.props.onEditAssignment(editedAssignment, true);
    } else {
      editedAssignment = {
        id: assignment.id,
        status: newStatus,
      };
      this.props.onEditAssignment(editedAssignment, true);
    }
  };

  showReassignModal = (assignment) => {
    if (this.props.writers) {
      this.props.openModal({
        modalContent: (
          <WritersContainer
            title="Reassign"
            closeModal={this.props.closeModal}
            forAssign
            assignment={assignment.id}
            rate={assignment.studyProgram.typeId.id}
            isQual={assignment.studyProgram.type === 'qual'}
            selectedWriterEmail={this.props.selectedWriter.email}
          />
        ),
        modalClassName: 'modal-create-chapter grey-modal-body',
      });
    } else {
      this.props.openModal({
        modalContent: (
          <WritersContainer
            title="Reassign"
            closeModal={this.props.closeModal}
            forAssign
            assignment={assignment.id}
            rate={this.props.selectedRate.id}
            isQual={!this.props.isViewRates}
          />
        ),
        modalClassName: 'modal-create-chapter grey-modal-body',
      });
    }
  };

  onSettingOptions = (assignment, option) => {
    switch (option) {
      case 'Open':
        this.props.onQuestionDetails(assignment);
        break;
      case 'Submit':
        this.onSubmitAssignment(assignment);
        break;
      case 'Start':
        this.onStatusChange(assignment, getStatusByName('In progress'));
        this.props.onQuestionDetails(assignment);
        break;
      case 'Review':
        if (this.props.writers) {
          assignment.writerEmail = this.props.selectedWriter.email;
        } else {
          assignment.writerEmail = null;
        }
        this.props.onQuestionOverview(assignment);
        break;
      case 'Reassign':
        this.showReassignModal(assignment);
        break;
      case 'Publish':
        this.onStatusChange(assignment, getStatusByName('Published'));
        break;
      case 'Delete':
        this.props.openModal({
          modalTitle: 'Deleting an assignment',
          modalContent: (
            <ConfirmDeletion
              onSubmit={() => {
                if (this.props.writers) {
                  this.props.onDelete(
                    assignment,
                    this.props.selectedWriter.email,
                  );
                } else {
                  this.props.onDelete(assignment);
                }
                this.props.closeModal();
              }}
              message="You are about to delete an assignment, all changes applied to the questions under it will be deleted"
              onCancel={this.props.closeModal}
            />
          ),
        });
        break;
      case 'Generate a Summary':
      case 'Regenerate a Summary':
        this.props.openModal({
          modalContent: (
            <QuestionGptModal
              onCancel={this.props.closeModal}
              chapter={assignment.chapter}
              title={option}
              option={
                option === 'Generate a Summary' ? 'generate' : 'regenerate'
              }
              getUrlPdf={this.props.getUrlPdf}
              createQuestion={false}
              assignment={assignment}
            />
          ),
          modalClassName: 'modal-question-container',
        });
        break;
      case 'View a Summary':
        let summary = assignment.summary;
        if (this.props.summary?.chapter === assignment.chapter.id)
          summary = this.props.summary;

        this.props.openModal({
          modalContent: (
            <ViewSummaryModal
              onCancel={this.props.closeModal}
              chapter={assignment.chapter}
              summary={summary}
            />
          ),
          modalClassName: 'viewComments',
        });

        break;
      case 'View comments':
        this.props.openModal({
          modalTitle: 'View comments',
          modalContent: (
            <AssignmentCommentsContainer
              assignment={assignment}
              canAddComment={this.canAddComment(assignment)}
            />
          ),
          cross: true,
          modalClassName: 'viewComments',
        });
        break;

      default:
        break;
    }
  };

  renderOptions = (options, assignment) => {
    if (options.length === 0) {
      return null;
    }
    return (
      <DropDown
        writers={this.props.writers}
        options={options.map((option) => ({
          name: option,
          event: () => this.onSettingOptions(assignment, option),
        }))}
        title="Assignment"
        style={{
          position: 'absolute',
          right: '0px',
        }}
      />
    );
  };

  dragStart = (e) => {
    this.dragged = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text', String(this.dragged.dataset.index));
  };

  dragEnd = () => {
    this.dragged = null;
  };

  dragOver = (e) => {
    e.preventDefault();
  };

  dragLeave = (e) => {
    e.preventDefault();
  };

  drop = (e) => {
    e.preventDefault();
    if (!this.canReorder()) return;

    const targetIndex = Number(e.currentTarget.dataset.index);
    const oldIndex = Number(e.dataTransfer.getData('text'));
    if (Number.isNaN(oldIndex) || Number.isNaN(targetIndex)) return;

    const items = [...this.props.assignments].sort(
      (a, b) => (a.presentationIndex ?? 0) - (b.presentationIndex ?? 0),
    );
    const newIndex = Math.max(0, Math.min(targetIndex, items.length - 1));

    const [movedAssignment] = items.splice(oldIndex, 1);
    items.splice(newIndex, 0, movedAssignment);

    items.forEach((assignment, idx) => {
      assignment.presentationIndex = idx + 1;
    });

    this.props.reorderAssignmentsLocal(items);

    this.props.onEditAssignment({
      id: movedAssignment.id,
      presentationIndex: newIndex + 1,
      writerEmail: this.props.selectedWriter?.email,
    });
  };

  canReorder = () => {
    const permissionPrefix = this.getPermissionPrefix();
    return (
      this.props.activeStatus === 'Working On' &&
      canPerformAction(`${permissionPrefix}.Assign`, this.props.permissions)
    );
  };

  hasDueDatePermission = (permissions) => {
    const permissionPrefix = this.getPermissionPrefix();
    return canPerformAction(`${permissionPrefix}.EditDueDate`, permissions);
  };

  renderRowsForWritersView = (summary) => {
    const cursor = { cursor: 'pointer' };
    const permissionPrefix = this.getPermissionPrefix();
    const canCreateAIQuestions = canPerformAction(
      `${permissionPrefix}.CreateAIQuestions`,
      this.props.permissions,
    );
    const showLeadColumn =
      this.props.writers && this.props.activeStatus === 'Working On';

    let assignmentsToRender = this.props.assignments;

    // order working on assignments by presentationIndex
    if (this.props.activeStatus === 'Working On') {
      assignmentsToRender = [...this.props.assignments].sort(
        (a, b) => (a.presentationIndex ?? 0) - (b.presentationIndex ?? 0),
      );
    }
    // order awaiting approval assignments by the selected sort column
    if (this.props.activeStatus === 'Awaiting approval') {
      assignmentsToRender = this.sortAssignments(this.props.assignments);
    }

    return assignmentsToRender.map((assignment, index) => {
      const amountOfQuestions = assignment.amountOfQuestions;
      const isCreatingAIQuestions =
        assignment.aiTask?.status === AI_TASK_STATUS.IN_PROGRESS &&
        assignment.aiTask?.type === AI_TASK_TYPE.QUESTION_GENERATION;
      const leadStatusDotClass =
        assignment.status === 'In_Progress'
          ? 'statusDotInProgress'
          : assignment.status === 'Not_Started'
            ? 'statusDotPending'
            : null;
      const leadStatusTitle =
        assignment.status === 'In_Progress'
          ? 'In progress'
          : assignment.status === 'Not_Started'
            ? 'Not started'
            : '';

      const assignedBy = assignment.createdBy
        ? `${assignment.createdBy.name} ${assignment.createdBy.lastName}`
        : '';
      const chapterName = assignment.chapter?.name ?? 'n/a';
      const chapterPdfpages = assignment.chapter?.pdfPages ?? 'n/a';
      const referenceName = assignment.chapter?.reference
        ? assignment.chapter.reference.name
        : '';
      const actions = this.getActions(
        this.getRowState(assignment),
        assignment,
        summary,
      );
      let rate;

      if (this.props.writers) {
        rate = assignment.studyProgram.typeId?.shortname ?? '';
      }

      return (
        <Row
          className="assignmentRow"
          key={`asr-${assignment.id}`}
          draggable={this.canReorder()}
          data-index={index}
          onDragStart={this.dragStart}
          onDragOver={this.dragOver}
          onDragLeave={this.dragLeave}
          onDrop={this.drop}
          onDragEnd={this.dragEnd}
        >
          {showLeadColumn ? (
            <>
              <Col md="auto" className="tHeader assignmentLeadColumn">
                <div className="referenceCellWithIndicator assignmentLeadIndicator">
                  {leadStatusDotClass && (
                    <span
                      className={leadStatusDotClass}
                      title={leadStatusTitle}
                      aria-hidden="true"
                    />
                  )}
                  {this.canReorder() && <span className="dragHandle">☰</span>}
                </div>
              </Col>
              <Col md={2} className="tHeader">
                <span className="referenceName" title={referenceName}>
                  {referenceName}
                </span>
              </Col>
            </>
          ) : (
            <Col md={2} className="tHeader">
              <span className="referenceName" title={referenceName}>
                {referenceName}
              </span>
            </Col>
          )}
          <Col md={2} className="tHeader">
            <div title={chapterName}>{chapterName}</div>
          </Col>
          <Col md={1} className="tHeader justify-content-center">
            {chapterPdfpages}
          </Col>
          <Col md={1} className="tHeader justify-content-center">
            <div style={{ width: '100px' }}>
              {this.props.activeStatus === 'Awaiting approval' ? (
                <div
                  title={
                    assignment.submittedDate
                      ? moment(assignment.submittedDate)
                          .utc()
                          .format('MM/DD/YYYY')
                      : ''
                  }
                >
                  {assignment.submittedDate
                    ? moment(assignment.submittedDate)
                        .utc()
                        .format('MM/DD/YYYY')
                    : 'No date'}
                </div>
              ) : this.hasDueDatePermission(this.props.permissions) ? (
                <SimpleDatePicker
                  date={
                    assignment.dueDate ? moment(assignment.dueDate).utc() : null
                  }
                  onChange={(date) => {
                    this.onDateChange(date, assignment);
                  }}
                  className={
                    moment().utc() > moment(assignment.dueDate).utc()
                      ? 'red'
                      : ''
                  }
                />
              ) : (
                <div title={moment(assignment.dueDate).utc()}>
                  {assignment.dueDate
                    ? moment(assignment.dueDate).utc().format('MM/DD/YYYY')
                    : 'No date'}
                </div>
              )}
            </div>
          </Col>
          <Col md={1} className="tHeader justify-content-center">
            {moment(assignment.createdAt).format('MM/DD/YYYY')}
          </Col>

          <Col md={1} className="tHeader justify-content-center">
            {moment(assignment.updatedAt).format('MM/DD/YYYY')}
          </Col>

          <Col md={1} className="tHeader justify-content-center">
            <div title={assignedBy}>{assignedBy}</div>
          </Col>

          <Col md={1} className="tHeader justify-content-center">
            <div
              title={this.renderCommentsNumber(assignment)}
              style={this.canViewComments(assignment) ? cursor : null}
              onClick={
                this.canViewComments(assignment)
                  ? () => this.viewComments(assignment)
                  : null
              }
              className={
                this.renderCommentsNumber(assignment) > 0
                  ? 'comments-display comment-show'
                  : 'comments-display comment-none'
              }
            >
              {this.renderCommentsNumber(assignment)}
            </div>
          </Col>
          <Col md={1} className="tHeader justify-content-center">
            {this.props.writers && <div title="RATE">{rate}</div>}
          </Col>
          <Col md={1} className="tHeader justify-content-center">
            {amountOfQuestions}
          </Col>
          <Col md={1} className="tHeader">
            <div className="textButtonAndDrop">
              {canCreateAIQuestions &&
                this.props.activeStatus !== 'Awaiting approval' &&
                (isCreatingAIQuestions ? (
                  <div className="creatingAIquestions">
                    <button disabled>
                      <i
                        className="fa fa-circle-o-notch fa-spin"
                        style={{
                          height: '1rem',
                          width: '0.5rem',
                          color: '#9f8fef',
                        }}
                      />
                      <span>Creating AI Questions...</span>
                    </button>
                  </div>
                ) : (
                  <div className="createGPTquestions">
                    <div
                      onClick={() =>
                        this.openCreteQuestionModal(assignment, true)
                      }
                    >
                      Create AI Questions
                    </div>
                  </div>
                ))}
              {actions?.buttonText && (
                <div
                  className="assignmentsBtn"
                  onClick={() =>
                    this.onSettingOptions(assignment, actions.buttonText)
                  }
                >
                  <div className="assignmentsBtnText">{actions.buttonText}</div>
                </div>
              )}
            </div>
          </Col>
          <Col md={1} className="tHeader containerAssignmentsBtn">
            {actions?.options &&
              this.renderOptions(actions.options, assignment)}
          </Col>
        </Row>
      );
    });
  };

  openCreteQuestionModal(assignment, createQuestion) {
    this.props.toggleModalVisibility({
      modalContent: (
        <QuestionGptModal
          onCancel={this.props.closeModal}
          loadingCreateGPTquestions={this.props.loadingCreateGPTquestions}
          assignment={assignment}
          getUrlPdf={this.props.getUrlPdf}
          title="Create GPT Questions"
          createQuestion={createQuestion}
        />
      ),
      modalClassName: 'modal-question-container',
    });
  }

  renderRows = (summary) => {
    const cursor = { cursor: 'pointer' };
    const adminuser = this.props.selectedAdmin.id;
    const permissionPrefix = this.getPermissionPrefix();
    const canCreateAIQuestions = canPerformAction(
      `${permissionPrefix}.CreateAIQuestions`,
      this.props.permissions,
    );

    return this.props.assignments
      .filter((assignment) =>
        assignment.assignedTo.find((admin) => admin.id === Number(adminuser)),
      )
      .map((assignment) => {
        const amountOfQuestions = assignment.amountOfQuestions;
        const isCreatingAIQuestions =
          assignment.aiTask?.status === AI_TASK_STATUS.IN_PROGRESS &&
          assignment.aiTask?.type === AI_TASK_TYPE.QUESTION_GENERATION;

        const assignedBy = assignment.createdBy
          ? `${assignment.createdBy.name} ${assignment.createdBy.lastName}`
          : '';
        const chapterName = assignment.chapter
          ? assignment.chapter.name
          : 'n/a';
        const PDFpages = assignment.chapter
          ? assignment.chapter.pdfPages
          : 'n/a';
        const referenceName = assignment.chapter?.reference?.name || '';
        const actions = this.getActions(
          this.getRowState(assignment),
          assignment,
          summary,
        );

        return (
          <Row className="assignmentRow" key={assignment.id}>
            <Col
              md={2}
              className="tHeader"
              style={{ overflowWrap: 'anywhere' }}
            >
              <div title={referenceName}>{referenceName}</div>
            </Col>
            <Col md={2} className="tHeader">
              <div title={chapterName}>{chapterName}</div>
            </Col>
            <Col md={1} className="tHeader justify-content-center">
              <div>{PDFpages}</div>
            </Col>
            <Col md={1} className="tHeader justify-content-center">
              <div style={{ width: '100px' }}>
                {this.hasDueDatePermission(this.props.permissions) ? (
                  <SimpleDatePicker
                    date={
                      assignment.dueDate
                        ? moment(assignment.dueDate).utc()
                        : null
                    }
                    onChange={(date) => {
                      this.onDateChange(date, assignment);
                    }}
                    className={
                      moment().utc() > moment(assignment.dueDate).utc()
                        ? 'red'
                        : ''
                    }
                  />
                ) : (
                  <div title={moment(assignment.dueDate).utc()}>
                    {assignment.dueDate
                      ? moment(assignment.dueDate).utc().format('MM/DD/YYYY')
                      : 'No date'}
                  </div>
                )}
              </div>
            </Col>
            <Col md={1} className="tHeader justify-content-center">
              {moment(assignment.createdAt).format('MM/DD/YYYY')}
            </Col>

            <Col md={1} className="tHeader justify-content-center">
              {moment(assignment.updatedAt).format('MM/DD/YYYY')}
            </Col>

            <Col md={1} className="tHeader justify-content-center">
              <div title={assignedBy}>{assignedBy}</div>
            </Col>
            <Col md={1} className="tHeader justify-content-center">
              <div
                title={this.renderCommentsNumber(assignment)}
                style={this.canViewComments(assignment) ? cursor : null}
                onClick={
                  this.canViewComments(assignment)
                    ? () => this.viewComments(assignment)
                    : null
                }
                className={
                  this.renderCommentsNumber(assignment) > 0
                    ? 'comments-display comment-show'
                    : 'comments-display comment-none'
                }
              >
                {this.renderCommentsNumber(assignment)}
              </div>
            </Col>
            <Col md={1} className="tHeader justify-content-center">
              {amountOfQuestions}
            </Col>
            <Col md={1} className="tHeader">
              <div className="textButtonAndDrop">
                {canCreateAIQuestions &&
                  (isCreatingAIQuestions ? (
                    <div className="creatingAIquestions">
                      <button disabled>
                        <i
                          className="fa fa-circle-o-notch fa-spin"
                          style={{
                            height: '1rem',
                            width: '0.5rem',
                            color: '#9f8fef',
                          }}
                        />
                        <span>Creating AI Questions...</span>
                      </button>
                    </div>
                  ) : (
                    <div className="createGPTquestions">
                      <div
                        onClick={() =>
                          this.openCreteQuestionModal(assignment, true)
                        }
                      >
                        Create GPT/AI Questions
                      </div>
                    </div>
                  ))}
                {actions?.buttonText && (
                  <div
                    className="assignmentsBtn"
                    onClick={() =>
                      this.onSettingOptions(assignment, actions.buttonText)
                    }
                  >
                    <div className="assignmentsBtnText">
                      {actions.buttonText}
                    </div>
                  </div>
                )}
              </div>
            </Col>
            <Col md={1} className="containerAssignmentsBtn">
              {actions?.options &&
                this.renderOptions(actions.options, assignment)}
            </Col>
          </Row>
        );
      });
  };

  render() {
    let rows = [];
    if (this.props.writers) {
      rows = this.renderRowsForWritersView(this.props.summary);
    } else {
      rows = this.renderRows(this.props.summary);
    }
    return (
      <div className="assignmentsTableContainer">
        {rows.length > 0 && this.renderHeader()}
        {rows.length > 0 ? (
          rows
        ) : (
          <div className="noAssignmentsText">No assignments to show</div>
        )}
      </div>
    );
  }
}
